import { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  createRestaurant,
  updateRestaurant,
  fetchRestaurants,
} from "@/store/restaurantsSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";

const schema = yup.object({
  name: yup.string().required(),
  categoryId: yup.string().required(),
  url: yup.string().optional().default(""),
  location: yup.string().optional().default(""),
  imageUrl: yup.string().optional().default(""),
});

type FormData = yup.InferType<typeof schema>;

export default function AddRestaurant() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { items: categories } = useAppSelector((s) => s.categories);
  const { items: restaurants } = useAppSelector((s) => s.restaurants);
  const { notify } = useNotification();

  const editing = id ? restaurants.find((r) => r.id === id) : null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      categoryId: "",
      url: "",
      location: "",
      imageUrl: "",
    },
  });

  useEffect(() => {
    dispatch(fetchCategories());
    if (id) dispatch(fetchRestaurants());
  }, [dispatch, id]);

  useEffect(() => {
    if (editing) {
      reset({
        name: editing.name,
        categoryId: editing.categoryId,
        url: editing.url || "",
        location: editing.location || "",
        imageUrl: editing.imageUrl || "",
      });
    }
  }, [editing, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (editing) {
        await dispatch(updateRestaurant({ id: editing.id, data })).unwrap();
        notify(t("restaurants.edit"), "success");
      } else {
        await dispatch(createRestaurant(data)).unwrap();
        notify(t("restaurants.add"), "success");
      }
      navigate("/restaurants");
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate("/restaurants")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          {editing ? t("restaurants.edit") : t("restaurants.add")}
        </Typography>
      </Box>
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("name")}
              label={t("restaurants.name")}
              placeholder={t("restaurants.namePlaceholder")}
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{ mb: 2 }}
            />
            <TextField
              {...register("categoryId")}
              select
              label={t("restaurants.category")}
              fullWidth
              error={!!errors.categoryId}
              helperText={errors.categoryId?.message}
              sx={{ mb: 2 }}
              defaultValue={editing?.categoryId || ""}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              {...register("location")}
              label={t("restaurants.location")}
              placeholder={t("restaurants.locationPlaceholder")}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              {...register("url")}
              label={t("restaurants.url")}
              placeholder="https://"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              {...register("imageUrl")}
              label={t("restaurants.imageUrl")}
              placeholder="https://"
              fullWidth
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => navigate("/restaurants")}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="contained">
                {editing ? t("common.update") : t("common.save")}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
